import { ApiError } from '@/api/errors'
import type {
  AdminScope,
  CapabilitySwitch,
  NodeRef,
  Organization,
  PendingInvitation,
  PriceRequest,
  PriceRequestInput,
  StaffMember,
  SwitchCapability,
  SwitchLevel,
} from '@/features/admin/types'
import { SWITCH_CAPABILITIES } from '@/features/admin/types'
import { demoInvitations } from '@/features/invitations/demo/demoInvitations'
import type { InviteInput } from '@/features/invitations/schemas/invitationSchemas'
import type { InvitationCreated } from '@/features/invitations/types'

/**
 * Fixtures and rules for the company and franchise admin panels (spec
 * `docs/superpowers/specs/2026-10-27-company-and-franchise-admin-design.md` §5, §7): the
 * organization tree, capability switches, staff, invitations and price requests all live
 * here, as one module, since the panels always read them together.
 *
 * This module must **never import `@/api/demoAuth`** (import-cycle risk — `demoAuth` sits
 * below `api/`, and other `api/*` files already import this module). Where it needs an id
 * `demoAuth` also defines, that id is copied below as a literal with a comment pointing back
 * at its source, and the signed-in user's id is passed in by the caller instead
 * (`currentDemoUserId()`, exported from `demoAuth.ts`, read by `staffApi.ts`).
 */

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function notFoundError(): ApiError {
  return new ApiError({ status: 404, code: 'NOT_FOUND', message: 'Nicht gefunden.' })
}

// ---------------------------------------------------------------------------
// Identity — copied literals, not imports (see module comment above).
// ---------------------------------------------------------------------------

/** `demoAuth.ts` `LOCATIONS[0].publicId` — the seed München Standort. */
const MUNICH_LOCATION_ID = '00000000-0000-0000-0000-000000000101'

/** `demoAuth.ts` `DEMO_ADMIN_USER.publicId` — the `unternehmen@ail-demo.local` login. */
const DEMO_COMPANY_ADMIN_ID = '00000000-0000-0000-0000-000000000002'

/** `demoAuth.ts` `DEMO_USER.publicId` — the `admin@ail-demo.local` studio login. */
const DEMO_STUDIO_ADMIN_ID = '00000000-0000-0000-0000-0000000000001'

/** A new franchise partner persona; Task 3 wires a login for it. */
export const DEMO_FRANCHISE_PARTNER_ID = '00000000-0000-0000-0000-000000000201'

const FRANCHISE_ZENTRALE_ID = '00000000-0000-0000-0000-000000000301'
const FRANCHISE_SUED_ID = '00000000-0000-0000-0000-000000000302'
const FRANCHISE_NORD_ID = '00000000-0000-0000-0000-000000000303'

const STUTTGART_FELLBACH_ID = '00000000-0000-0000-0000-000000000401'
const LUDWIGSBURG_ID = '00000000-0000-0000-0000-000000000402'
const ESSLINGEN_ID = '00000000-0000-0000-0000-000000000403'
const HAMBURG_ID = '00000000-0000-0000-0000-000000000404'
const BREMEN_ID = '00000000-0000-0000-0000-000000000405'

const PRICE_REQUEST_OPEN_1_ID = '00000000-0000-0000-0000-000000000501'
const PRICE_REQUEST_OPEN_2_ID = '00000000-0000-0000-0000-000000000502'
const PRICE_REQUEST_APPROVED_ID = '00000000-0000-0000-0000-000000000503'
const PRICE_REQUEST_REJECTED_ID = '00000000-0000-0000-0000-000000000504'

const INVITATION_1_ID = '00000000-0000-0000-0000-000000000601'
const INVITATION_2_ID = '00000000-0000-0000-0000-000000000602'

/** Stable handles for tests, so they never guess at a fixture id. */
export const DEMO_ADMIN_IDS = {
  company: '00000000-0000-0000-0000-000000000001',
  franchiseZentrale: FRANCHISE_ZENTRALE_ID,
  franchiseSued: FRANCHISE_SUED_ID,
  franchiseNord: FRANCHISE_NORD_ID,
  locationMunich: MUNICH_LOCATION_ID,
  locationStuttgartFellbach: STUTTGART_FELLBACH_ID,
  locationLudwigsburg: LUDWIGSBURG_ID,
  locationEsslingen: ESSLINGEN_ID,
  locationHamburg: HAMBURG_ID,
  locationBremen: BREMEN_ID,
  staffCompanyAdmin: DEMO_COMPANY_ADMIN_ID,
  staffFranchisePartner: DEMO_FRANCHISE_PARTNER_ID,
  staffStudioAdmin: DEMO_STUDIO_ADMIN_ID,
  priceRequestOpen1: PRICE_REQUEST_OPEN_1_ID,
  priceRequestOpen2: PRICE_REQUEST_OPEN_2_ID,
  priceRequestApproved: PRICE_REQUEST_APPROVED_ID,
  priceRequestRejected: PRICE_REQUEST_REJECTED_ID,
  invitation1: INVITATION_1_ID,
  invitation2: INVITATION_2_ID,
} as const

// ---------------------------------------------------------------------------
// Organization tree
// ---------------------------------------------------------------------------

interface LocationFixture {
  publicId: string
  name: string
  timezone: string
}

interface FranchiseFixture {
  publicId: string
  name: string
  kind: string
  kindLabel: string
  partners: { publicId: string; displayName: string }[]
  locations: LocationFixture[]
}

const COMPANY = { publicId: DEMO_ADMIN_IDS.company, name: 'AiL GmbH' }

const FRANCHISES: FranchiseFixture[] = [
  {
    publicId: FRANCHISE_ZENTRALE_ID,
    name: 'Zentrale',
    kind: 'company_owned',
    kindLabel: 'Eigenbetrieb',
    partners: [],
    locations: [{ publicId: MUNICH_LOCATION_ID, name: 'München', timezone: 'Europe/Berlin' }],
  },
  {
    publicId: FRANCHISE_SUED_ID,
    name: 'Franchise Süd',
    kind: 'standard',
    kindLabel: 'Franchise',
    partners: [{ publicId: DEMO_FRANCHISE_PARTNER_ID, displayName: 'Julia Franchise' }],
    locations: [
      { publicId: STUTTGART_FELLBACH_ID, name: 'Stuttgart Fellbach', timezone: 'Europe/Berlin' },
      { publicId: LUDWIGSBURG_ID, name: 'Ludwigsburg', timezone: 'Europe/Berlin' },
      { publicId: ESSLINGEN_ID, name: 'Esslingen', timezone: 'Europe/Berlin' },
    ],
  },
  {
    publicId: FRANCHISE_NORD_ID,
    name: 'Franchise Nord',
    kind: 'standard',
    kindLabel: 'Franchise',
    partners: [],
    locations: [
      { publicId: HAMBURG_ID, name: 'Hamburg', timezone: 'Europe/Berlin' },
      { publicId: BREMEN_ID, name: 'Bremen', timezone: 'Europe/Berlin' },
    ],
  },
]

/** Every location's owning franchise, so staff/switches/invitations can scope by franchise. */
const LOCATION_TO_FRANCHISE = new Map<string, string>(
  FRANCHISES.flatMap((franchise) =>
    franchise.locations.map((location) => [location.publicId, franchise.publicId] as const),
  ),
)

function findFranchise(id: string): FranchiseFixture | undefined {
  return FRANCHISES.find((franchise) => franchise.publicId === id)
}

function findLocation(
  id: string,
): { location: LocationFixture; franchise: FranchiseFixture } | undefined {
  for (const franchise of FRANCHISES) {
    const location = franchise.locations.find((candidate) => candidate.publicId === id)
    if (location) return { location, franchise }
  }
  return undefined
}

/**
 * The caller's slice, as the server scopes it: a franchise partner sees only the
 * franchises it is partner of; anyone else (head office) sees the whole company.
 */
async function fetchOrganization(selfId: string | null = null): Promise<Organization> {
  await wait(200)
  const own = FRANCHISES.filter((franchise) =>
    franchise.partners.some((partner) => partner.publicId === selfId),
  )
  return {
    company: { ...COMPANY },
    franchises: [...(own.length > 0 ? own : FRANCHISES)]
      .sort((a, b) => a.name.localeCompare(b.name, 'de'))
      .map((franchise) => ({
        publicId: franchise.publicId,
        name: franchise.name,
        kind: franchise.kind,
        kindLabel: franchise.kindLabel,
        partners: franchise.partners.map((partner) => ({ ...partner })),
        locations: [...franchise.locations]
          .sort((a, b) => a.name.localeCompare(b.name, 'de'))
          .map((location) => ({
            publicId: location.publicId,
            name: location.name,
            timezone: location.timezone,
            staffCount: STAFF.filter((member) =>
              member.assignments.some(
                (assignment) => assignment.node.publicId === location.publicId,
              ),
            ).length,
          })),
      })),
  }
}

// ---------------------------------------------------------------------------
// Switches
// ---------------------------------------------------------------------------

interface SwitchState {
  allowed: boolean
  changedBy: string | null
  changedAt: string | null
}

const SWITCH_LABELS: Record<SwitchCapability, { label: string; description: string }> = {
  REFUND: { label: 'Erstattungen', description: 'Käufe an Kunden erstatten' },
  PAYMENT_PLAN: {
    label: 'Ratenzahlung & Abos',
    description: 'Ratenzahlungen und Abonnements anbieten',
  },
  INVITE_STAFF: {
    label: 'Mitarbeiter einladen',
    description: 'Neue Mitarbeiter in dieses Team einladen',
  },
  INVITE_MANAGER: {
    label: 'Filialleiter einladen',
    description: 'Neue Filialleiter einladen',
  },
}

function defaultSwitchRecord(): Record<SwitchCapability, SwitchState> {
  const entries = SWITCH_CAPABILITIES.map(
    (capability) => [capability, { allowed: true, changedBy: null, changedAt: null }] as const,
  )
  return Object.fromEntries(entries) as Record<SwitchCapability, SwitchState>
}

const franchiseSwitches = new Map<string, Record<SwitchCapability, SwitchState>>(
  FRANCHISES.map((franchise) => [franchise.publicId, defaultSwitchRecord()]),
)

const locationSwitches = new Map<string, Record<SwitchCapability, SwitchState>>(
  FRANCHISES.flatMap((franchise) =>
    franchise.locations.map((location) => [location.publicId, defaultSwitchRecord()] as const),
  ),
)

// Seed deviations so every switch state shows (brief §demo fixtures): a franchise-level deny
// and a Standort-level deny.
franchiseSwitches.set(FRANCHISE_NORD_ID, {
  ...defaultSwitchRecord(),
  PAYMENT_PLAN: {
    allowed: false,
    changedBy: 'Unternehmensadmin',
    changedAt: '2026-10-20T09:00:00+02:00',
  },
})
locationSwitches.set(LUDWIGSBURG_ID, {
  ...defaultSwitchRecord(),
  INVITE_MANAGER: {
    allowed: false,
    changedBy: 'Julia Franchise',
    changedAt: '2026-10-22T14:30:00+02:00',
  },
})

function toCapabilitySwitch(
  capability: SwitchCapability,
  state: SwitchState,
  allowedAbove: boolean,
): CapabilitySwitch {
  return {
    capability,
    label: SWITCH_LABELS[capability].label,
    description: SWITCH_LABELS[capability].description,
    allowed: state.allowed,
    allowedAbove,
    changedBy: state.changedBy,
    changedAt: state.changedAt,
  }
}

async function fetchSwitches(level: SwitchLevel, id: string): Promise<CapabilitySwitch[]> {
  await wait(200)

  if (level === 'franchise') {
    const record = franchiseSwitches.get(id)
    if (!record) throw notFoundError()
    return SWITCH_CAPABILITIES.map((capability) =>
      toCapabilitySwitch(capability, record[capability], true),
    )
  }

  const record = locationSwitches.get(id)
  if (!record) throw notFoundError()
  const franchiseId = LOCATION_TO_FRANCHISE.get(id)
  const franchiseRecord = franchiseId ? franchiseSwitches.get(franchiseId) : undefined
  return SWITCH_CAPABILITIES.map((capability) =>
    toCapabilitySwitch(
      capability,
      record[capability],
      franchiseRecord?.[capability].allowed ?? true,
    ),
  )
}

async function setSwitch(change: {
  level: SwitchLevel
  id: string
  capability: string
  allowed: boolean
}): Promise<CapabilitySwitch> {
  await wait(250)

  const capability = SWITCH_CAPABILITIES.find((candidate) => candidate === change.capability)
  if (!capability) {
    throw new ApiError({
      status: 422,
      code: 'VALIDATION_FAILED',
      message: 'Die Eingabe ist unvollständig oder ungültig.',
      fieldErrors: { capability: 'must be one of the known capabilities' },
    })
  }

  // No actor identity is available here by design (see module comment) — the demo attributes
  // every change to the acting seat generically, as a real audit trail would to "you".
  const changedBy = 'Sie'
  const changedAt = new Date().toISOString()

  if (change.level === 'franchise') {
    const record = franchiseSwitches.get(change.id)
    if (!record) throw notFoundError()
    record[capability] = { allowed: change.allowed, changedBy, changedAt }
    return toCapabilitySwitch(capability, record[capability], true)
  }

  const record = locationSwitches.get(change.id)
  if (!record) throw notFoundError()
  const franchiseId = LOCATION_TO_FRANCHISE.get(change.id)
  const franchiseRecord = franchiseId ? franchiseSwitches.get(franchiseId) : undefined
  const allowedAbove = franchiseRecord?.[capability].allowed ?? true

  if (change.allowed && !allowedAbove) {
    throw new ApiError({
      status: 409,
      code: 'SWITCH_LOCKED_ABOVE',
      message: 'Diese Funktion ist von der Zentrale für die ganze Franchise gesperrt.',
    })
  }

  record[capability] = { allowed: change.allowed, changedBy, changedAt }
  return toCapabilitySwitch(capability, record[capability], allowedAbove)
}

/**
 * The effective state of all four capabilities at a Standort — `allowed && allowedAbove` per
 * capability. Exported standalone (not a `demoAdmin` method) so Task 7 can fold it into
 * `demoAuth`'s session fixtures without this module importing that one (see module comment).
 */
export function demoSwitchesFor(locationId: string): Record<string, boolean> {
  const record = locationSwitches.get(locationId)
  const franchiseId = LOCATION_TO_FRANCHISE.get(locationId)
  const franchiseRecord = franchiseId ? franchiseSwitches.get(franchiseId) : undefined

  const result: Record<string, boolean> = {}
  for (const capability of SWITCH_CAPABILITIES) {
    const own = record?.[capability].allowed ?? true
    const above = franchiseRecord?.[capability].allowed ?? true
    result[capability] = own && above
  }
  return result
}

// ---------------------------------------------------------------------------
// Staff
// ---------------------------------------------------------------------------

interface StaffFixture {
  publicId: string
  displayName: string
  email: string
  status: string
  statusLabel: string
  lastLoginAt: string | null
  assignments: { role: { key: string; label: string }; node: NodeRef }[]
}

const ROLE_LABELS: Record<string, string> = {
  company_admin: 'Unternehmensadmin',
  franchise_partner: 'Franchisenehmer',
  branch_manager: 'Filialleiter',
  branch_staff: 'Mitarbeiter',
}

function role(key: string): { key: string; label: string } {
  return { key, label: ROLE_LABELS[key] ?? key }
}

const companyNode: NodeRef = { level: 'company', publicId: COMPANY.publicId, name: COMPANY.name }
const suedNode: NodeRef = { level: 'franchise', publicId: FRANCHISE_SUED_ID, name: 'Franchise Süd' }
const munichNode: NodeRef = { level: 'location', publicId: MUNICH_LOCATION_ID, name: 'München' }
const stuttgartNode: NodeRef = {
  level: 'location',
  publicId: STUTTGART_FELLBACH_ID,
  name: 'Stuttgart Fellbach',
}
const ludwigsburgNode: NodeRef = {
  level: 'location',
  publicId: LUDWIGSBURG_ID,
  name: 'Ludwigsburg',
}
const esslingenNode: NodeRef = { level: 'location', publicId: ESSLINGEN_ID, name: 'Esslingen' }
const hamburgNode: NodeRef = { level: 'location', publicId: HAMBURG_ID, name: 'Hamburg' }
const bremenNode: NodeRef = { level: 'location', publicId: BREMEN_ID, name: 'Bremen' }

const STAFF: StaffFixture[] = [
  {
    publicId: DEMO_COMPANY_ADMIN_ID,
    displayName: 'Unternehmensadmin',
    email: 'unternehmen@ail-demo.local',
    status: 'active',
    statusLabel: 'Aktiv',
    lastLoginAt: '2026-10-27T08:00:00+01:00',
    assignments: [{ role: role('company_admin'), node: companyNode }],
  },
  {
    publicId: DEMO_FRANCHISE_PARTNER_ID,
    displayName: 'Julia Franchise',
    email: 'julia.franchise@ail-demo.local',
    status: 'active',
    statusLabel: 'Aktiv',
    lastLoginAt: '2026-10-26T17:45:00+01:00',
    assignments: [{ role: role('franchise_partner'), node: suedNode }],
  },
  {
    publicId: DEMO_STUDIO_ADMIN_ID,
    displayName: 'Admin',
    email: 'admin@ail-demo.local',
    status: 'active',
    statusLabel: 'Aktiv',
    lastLoginAt: '2026-10-27T07:30:00+01:00',
    assignments: [{ role: role('branch_manager'), node: munichNode }],
  },
  {
    publicId: '00000000-0000-0000-0000-000000000701',
    displayName: 'Anna Weber',
    email: 'anna.weber@ail-demo.local',
    status: 'active',
    statusLabel: 'Aktiv',
    lastLoginAt: '2026-10-26T09:10:00+01:00',
    assignments: [{ role: role('branch_manager'), node: munichNode }],
  },
  {
    publicId: '00000000-0000-0000-0000-000000000702',
    displayName: 'Tom Ferber',
    email: 'tom.ferber@ail-demo.local',
    status: 'active',
    statusLabel: 'Aktiv',
    lastLoginAt: '2026-10-25T13:20:00+01:00',
    assignments: [{ role: role('branch_staff'), node: munichNode }],
  },
  {
    publicId: '00000000-0000-0000-0000-000000000703',
    displayName: 'Petra Stuttgart',
    email: 'petra.stuttgart@ail-demo.local',
    status: 'active',
    statusLabel: 'Aktiv',
    lastLoginAt: '2026-10-25T08:05:00+02:00',
    assignments: [{ role: role('branch_manager'), node: stuttgartNode }],
  },
  {
    publicId: '00000000-0000-0000-0000-000000000704',
    displayName: 'Markus Ludwig',
    email: 'markus.ludwig@ail-demo.local',
    status: 'active',
    statusLabel: 'Aktiv',
    lastLoginAt: '2026-10-24T11:40:00+02:00',
    assignments: [{ role: role('branch_staff'), node: ludwigsburgNode }],
  },
  {
    publicId: '00000000-0000-0000-0000-000000000705',
    displayName: 'Sabine Esslinger',
    email: 'sabine.esslinger@ail-demo.local',
    status: 'disabled',
    statusLabel: 'Gesperrt',
    lastLoginAt: '2026-09-30T10:00:00+02:00',
    assignments: [{ role: role('branch_staff'), node: esslingenNode }],
  },
  {
    publicId: '00000000-0000-0000-0000-000000000706',
    displayName: 'Nils Hansen',
    email: 'nils.hansen@ail-demo.local',
    status: 'active',
    statusLabel: 'Aktiv',
    lastLoginAt: '2026-10-26T08:50:00+02:00',
    assignments: [{ role: role('branch_manager'), node: hamburgNode }],
  },
  {
    publicId: '00000000-0000-0000-0000-000000000707',
    displayName: 'Greta Bremer',
    email: 'greta.bremer@ail-demo.local',
    status: 'active',
    statusLabel: 'Aktiv',
    lastLoginAt: '2026-10-23T15:15:00+02:00',
    assignments: [{ role: role('branch_staff'), node: bremenNode }],
  },
]

function findStaff(id: string): StaffFixture | undefined {
  return STAFF.find((member) => member.publicId === id)
}

/**
 * Can this caller manage this member? The demo has no per-request caller identity for a plain
 * `list()` (only `block()` is handed one — see module comment), so it approximates the spec's
 * "never the caller's superiors" rule structurally: a company admin or franchise partner is
 * never manageable from these panels, a Filialleiter/Mitarbeiter row always is. Self-exclusion
 * is still enforced exactly, at `block()` time, from the id the caller passes in.
 */
function canManageRow(member: StaffFixture): boolean {
  return member.assignments.every(
    (assignment) =>
      assignment.role.key === 'branch_manager' || assignment.role.key === 'branch_staff',
  )
}

function toStaffMemberView(member: StaffFixture): StaffMember {
  return {
    publicId: member.publicId,
    displayName: member.displayName,
    email: member.email,
    status: member.status,
    statusLabel: member.statusLabel,
    lastLoginAt: member.lastLoginAt,
    assignments: member.assignments.map((assignment) => ({
      role: { ...assignment.role },
      node: { ...assignment.node },
    })),
    canManage: canManageRow(member),
  }
}

function nodeFranchiseId(node: NodeRef): string | null {
  if (node.level === 'franchise') return node.publicId
  if (node.level === 'location') return LOCATION_TO_FRANCHISE.get(node.publicId) ?? null
  return null
}

function matchesScope(member: StaffFixture, scope: AdminScope): boolean {
  if ('franchise' in scope) {
    return member.assignments.some(
      (assignment) => nodeFranchiseId(assignment.node) === scope.franchise,
    )
  }
  if ('location' in scope) {
    return member.assignments.some(
      (assignment) =>
        assignment.node.level === 'location' && assignment.node.publicId === scope.location,
    )
  }
  // No param — "whole caller scope" in the contract; the demo has only one company, so that
  // is every row (matches the company admin's view, the only "whole scope" caller today).
  return true
}

async function listStaff(scope: AdminScope): Promise<StaffMember[]> {
  await wait(250)
  return STAFF.filter((member) => matchesScope(member, scope))
    .map(toStaffMemberView)
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'de'))
}

async function block(id: string, selfId: string | null): Promise<StaffMember> {
  await wait(300)
  if (selfId !== null && id === selfId) {
    throw new ApiError({
      status: 409,
      code: 'CANNOT_MANAGE_SELF',
      message: 'Sie können Ihr eigenes Konto nicht sperren.',
    })
  }
  const member = findStaff(id)
  if (!member) throw notFoundError()
  member.status = 'disabled'
  member.statusLabel = 'Gesperrt'
  return toStaffMemberView(member)
}

async function unblock(id: string): Promise<StaffMember> {
  await wait(300)
  const member = findStaff(id)
  if (!member) throw notFoundError()
  member.status = 'active'
  member.statusLabel = 'Aktiv'
  return toStaffMemberView(member)
}

// ---------------------------------------------------------------------------
// Invitations (admin-wide view — distinct from the per-Standort invite/accept flow in
// `features/invitations/demo/demoInvitations.ts`, which this module calls into so a demo
// invite created here can still be previewed and accepted with its token).
// ---------------------------------------------------------------------------

const INVITATIONS: PendingInvitation[] = [
  {
    publicId: INVITATION_1_ID,
    email: 'neu.stuttgart@ail-demo.local',
    fullName: 'Lena Beispiel',
    role: role('branch_staff'),
    node: stuttgartNode,
    invitedBy: 'Julia Franchise',
    createdAt: '2026-10-25T09:00:00+02:00',
    expiresAt: '2026-10-28T09:00:00+02:00',
  },
  {
    publicId: INVITATION_2_ID,
    email: 'neu.hamburg@ail-demo.local',
    fullName: 'Jonas Nord',
    role: role('branch_manager'),
    node: hamburgNode,
    invitedBy: 'Unternehmensadmin',
    createdAt: '2026-10-26T10:00:00+02:00',
    expiresAt: '2026-10-29T10:00:00+02:00',
  },
]

function matchesInvitationScope(invitation: PendingInvitation, scope: AdminScope): boolean {
  if ('franchise' in scope) return nodeFranchiseId(invitation.node) === scope.franchise
  if ('location' in scope) {
    return invitation.node.level === 'location' && invitation.node.publicId === scope.location
  }
  return true
}

async function listInvitations(scope: AdminScope): Promise<PendingInvitation[]> {
  await wait(200)
  return INVITATIONS.filter((invitation) => matchesInvitationScope(invitation, scope)).map(
    (invitation) => ({
      ...invitation,
    }),
  )
}

async function revokeInvitation(id: string): Promise<void> {
  await wait(200)
  const index = INVITATIONS.findIndex((invitation) => invitation.publicId === id)
  if (index === -1) throw notFoundError()
  INVITATIONS.splice(index, 1)
}

function resolveNode(node: { franchise?: string; location?: string }): NodeRef {
  if (node.franchise) {
    const franchise = findFranchise(node.franchise)
    if (!franchise) throw notFoundError()
    return { level: 'franchise', publicId: franchise.publicId, name: franchise.name }
  }
  if (node.location) {
    const found = findLocation(node.location)
    if (!found) throw notFoundError()
    return { level: 'location', publicId: found.location.publicId, name: found.location.name }
  }
  return companyNode
}

async function createInvitation(
  input: InviteInput,
  node: { franchise?: string; location?: string },
): Promise<InvitationCreated> {
  const created = await demoInvitations.create(input)
  INVITATIONS.unshift({
    publicId: created.publicId,
    email: created.email,
    fullName: created.fullName,
    role: role(created.role),
    node: resolveNode(node),
    // No caller identity is available here (see module comment) — the real server fills
    // this from the session, which the demo has no equivalent of at this layer.
    invitedBy: null,
    createdAt: new Date().toISOString(),
    expiresAt: created.expiresAt,
  })
  return created
}

// ---------------------------------------------------------------------------
// Price requests
// ---------------------------------------------------------------------------

const PRICE_REQUEST_KIND_LABELS: Record<string, string> = {
  trial: 'Probebehandlung',
  package: 'Paket',
  subscription: 'Abo',
}

let priceRequests: PriceRequest[] = [
  {
    id: PRICE_REQUEST_OPEN_1_ID,
    franchise: 'Franchise Süd',
    status: 'open',
    statusLabel: 'Offen',
    kind: 'package',
    kindLabel: PRICE_REQUEST_KIND_LABELS.package,
    creditQty: 10,
    priceGross: 990,
    currency: 'EUR',
    rateCount: 1,
    termPeriods: null,
    note: 'Saisonpaket für Neukunden.',
    requestedBy: 'Julia Franchise',
    createdAt: '2026-10-24T09:00:00+02:00',
    decidedBy: null,
    decidedAt: null,
    decisionNote: null,
    productId: null,
  },
  {
    id: PRICE_REQUEST_OPEN_2_ID,
    franchise: 'Franchise Süd',
    status: 'open',
    statusLabel: 'Offen',
    kind: 'subscription',
    kindLabel: PRICE_REQUEST_KIND_LABELS.subscription,
    creditQty: 4,
    priceGross: 59,
    currency: 'EUR',
    rateCount: 1,
    termPeriods: 12,
    note: null,
    requestedBy: 'Julia Franchise',
    createdAt: '2026-10-25T11:30:00+02:00',
    decidedBy: null,
    decidedAt: null,
    decisionNote: null,
    productId: null,
  },
  {
    id: PRICE_REQUEST_APPROVED_ID,
    franchise: 'Franchise Nord',
    status: 'approved',
    statusLabel: 'Genehmigt',
    kind: 'trial',
    kindLabel: PRICE_REQUEST_KIND_LABELS.trial,
    creditQty: 1,
    priceGross: 29,
    currency: 'EUR',
    rateCount: 1,
    termPeriods: null,
    note: null,
    requestedBy: 'Nils Hansen',
    createdAt: '2026-10-18T08:00:00+02:00',
    decidedBy: 'Unternehmensadmin',
    decidedAt: '2026-10-19T09:00:00+02:00',
    decisionNote: null,
    productId: '501',
  },
  {
    id: PRICE_REQUEST_REJECTED_ID,
    franchise: 'Franchise Nord',
    status: 'rejected',
    statusLabel: 'Abgelehnt',
    kind: 'package',
    kindLabel: PRICE_REQUEST_KIND_LABELS.package,
    creditQty: 20,
    priceGross: 1800,
    currency: 'EUR',
    rateCount: 1,
    termPeriods: null,
    note: 'Großpaket für Firmenkunden.',
    requestedBy: 'Greta Bremer',
    createdAt: '2026-10-21T08:15:00+01:00',
    decidedBy: 'Unternehmensadmin',
    decidedAt: '2026-10-22T09:30:00+01:00',
    decisionNote: 'Bitte zunächst mit einem kleineren Paket starten.',
    productId: null,
  },
]

/** A partner sees its own franchises' requests; head office sees the company's. */
async function listPriceRequests(selfId: string | null = null): Promise<PriceRequest[]> {
  await wait(200)
  const own = FRANCHISES.filter((franchise) =>
    franchise.partners.some((partner) => partner.publicId === selfId),
  ).map((franchise) => franchise.name)
  return priceRequests
    .filter((request) => own.length === 0 || own.includes(request.franchise))
    .map((request) => ({ ...request }))
}

function requireOpenPriceRequestIndex(id: string): number {
  const index = priceRequests.findIndex((request) => request.id === id)
  if (index === -1) throw notFoundError()
  if (priceRequests[index].status !== 'open') {
    throw new ApiError({
      status: 409,
      code: 'REQUEST_ALREADY_DECIDED',
      message: 'Diese Anfrage wurde bereits entschieden.',
    })
  }
  return index
}

async function createPriceRequest(input: PriceRequestInput): Promise<PriceRequest> {
  await wait(300)
  const created: PriceRequest = {
    id: crypto.randomUUID(),
    // The active Standort's franchise — the demo's one price-requesting persona is Julia
    // Franchise, so every demo-created request is hers until Task 3 adds more partners.
    franchise: 'Franchise Süd',
    status: 'open',
    statusLabel: 'Offen',
    kind: input.kind,
    kindLabel: PRICE_REQUEST_KIND_LABELS[input.kind] ?? input.kind,
    creditQty: input.creditQty,
    priceGross: input.priceGross,
    currency: 'EUR',
    rateCount: input.rateCount,
    termPeriods: input.kind === 'subscription' ? input.termPeriods : null,
    note: input.note.trim() === '' ? null : input.note,
    requestedBy: 'Julia Franchise',
    createdAt: new Date().toISOString(),
    decidedBy: null,
    decidedAt: null,
    decisionNote: null,
    productId: null,
  }
  priceRequests = [created, ...priceRequests]
  return { ...created }
}

async function approvePriceRequest(
  id: string,
  input: { ablefyId: number; ablefyPricingPlanId: number; name: string; paymentUrl: string },
): Promise<PriceRequest> {
  await wait(300)
  const index = requireOpenPriceRequestIndex(id)
  const updated: PriceRequest = {
    ...priceRequests[index],
    status: 'approved',
    statusLabel: 'Genehmigt',
    decidedBy: 'Unternehmensadmin',
    decidedAt: new Date().toISOString(),
    decisionNote: null,
    productId: String(input.ablefyId),
  }
  priceRequests = priceRequests.map((request, i) => (i === index ? updated : request))
  return { ...updated }
}

async function rejectPriceRequest(id: string, reason: string): Promise<PriceRequest> {
  await wait(300)
  const index = requireOpenPriceRequestIndex(id)
  const updated: PriceRequest = {
    ...priceRequests[index],
    status: 'rejected',
    statusLabel: 'Abgelehnt',
    decidedBy: 'Unternehmensadmin',
    decidedAt: new Date().toISOString(),
    decisionNote: reason,
  }
  priceRequests = priceRequests.map((request, i) => (i === index ? updated : request))
  return { ...updated }
}

async function withdrawPriceRequest(id: string): Promise<PriceRequest> {
  await wait(300)
  const index = requireOpenPriceRequestIndex(id)
  const updated: PriceRequest = {
    ...priceRequests[index],
    status: 'withdrawn',
    statusLabel: 'Zurückgezogen',
  }
  priceRequests = priceRequests.map((request, i) => (i === index ? updated : request))
  return { ...updated }
}

// ---------------------------------------------------------------------------

export const demoAdmin = {
  fetchOrganization,
  fetchSwitches,
  setSwitch,
  listStaff,
  block,
  unblock,
  listInvitations,
  revokeInvitation,
  createInvitation,
  listPriceRequests,
  createPriceRequest,
  approvePriceRequest,
  rejectPriceRequest,
  withdrawPriceRequest,
}
